// frontend/src/pages/customer/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiShoppingCart,
  FiPackage,
  FiTag,
  FiTruck,
  FiShield,
  FiArrowLeft,
  FiMinus,
  FiPlus,
  FiAlertCircle,
  FiCheck
} from 'react-icons/fi';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  const baseURL = process.env.REACT_APP_API_URL || '${process.env.REACT_APP_API_URL}';
  
  if (imagePath.startsWith('/uploads')) {
    return `${baseURL}${imagePath}`;
  }
  
  return `${baseURL}/uploads/${imagePath}`;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loadCartCount } = useOutletContext();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

 const fetchProduct = async () => {
  setLoading(true);
  try {
    const response = await axios.get(`/api/customer/products/${id}`);
    
    if (response.data.success) {
      const productData = response.data.data.product;
      
      // ✅ Parse images array
      let parsedImages = [];
      if (productData.images) {
        try {
          if (typeof productData.images === 'string') {
            parsedImages = JSON.parse(productData.images);
          } else if (Array.isArray(productData.images)) {
            parsedImages = productData.images;
          }
        } catch (err) {
          console.log('⚠️ Parse images error');
          parsedImages = productData.image_url ? [productData.image_url] : [];
        }
      } else if (productData.image_url) {
        parsedImages = [productData.image_url];
      }
      
      // Set product with parsed images
      setProduct({
        ...productData,
        images: parsedImages
      });
      
      // Set first image as selected
      setSelectedImage(parsedImages[0] || productData.image_url);
    }
  } catch (error) {
      console.error('❌ Fetch product error:', error);
      toast.error('Барааны мэдээлэл татахад алдаа гарлаа');
      navigate('/customer/products');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = () => {
    if (!product) return;

    const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
    const existingItem = cart.find(item => item.productId === product.id);
    
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push({
        productId: product.id,
        quantity: quantity
      });
    }

    localStorage.setItem('customerCart', JSON.stringify(cart));
    
    if (loadCartCount) {
      loadCartCount();
    }
    
    toast.success(`"${product.name}" сагсанд нэмэгдлээ (${quantity} ширхэг)`);
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= (product?.stock_quantity || 1)) {
      setQuantity(newQuantity);
    }
  };

  const getPrice = () => {
    if (!product) return 0;
    
    if (user?.customer_type === 'wholesale' && 
        quantity >= product.wholesale_quantity) {
      return product.wholesale_price;
    }
    
    return product.retail_price;
  };

  const getTotalPrice = () => {
    return getPrice() * quantity;
  };

  const getSavings = () => {
    if (!product) return 0;
    
    if (user?.customer_type === 'wholesale' && 
        quantity >= product.wholesale_quantity) {
      return (product.retail_price - product.wholesale_price) * quantity;
    }
    
    return 0;
  };

  if (loading) {
    return (
      <div className="container-custom py-12">
        <div className="text-center">
          <div className="spinner-lg mx-auto mb-4"></div>
          <p className="text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-custom py-12">
        <div className="text-center">
          <FiAlertCircle size={64} className="mx-auto mb-4 text-red-400" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Бараа олдсонгүй</h2>
          <p className="text-gray-600 mb-6">Энэ бараа устсан эсвэл идэвхгүй байна</p>
          <Link to="/customer/products" className="btn btn-primary">
            <FiArrowLeft className="mr-2" />
            Буцах
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="container-custom py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6">
          <Link to="/customer/products" className="text-blue-600 hover:text-blue-700">
            Бүтээгдэхүүн
          </Link>
          <span className="text-gray-400">/</span>
          {product.category_name && (
            <>
              <Link 
                to={`/customer/products?category=${product.category_id}`}
                className="text-blue-600 hover:text-blue-700"
              >
                {product.category_name}
              </Link>
              <span className="text-gray-400">/</span>
            </>
          )}
          <span className="text-gray-600">{product.name}</span>
        </nav>

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="btn btn-secondary mb-6"
        >
          <FiArrowLeft className="mr-2" />
          Буцах
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Product Images */}
          <div>
            <div className="card">
              {/* Main Image */}
              <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4">
                {selectedImage ? (
                  <img
                    src={getImageUrl(selectedImage)}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.nextElementSibling;
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className="w-full h-full flex items-center justify-center"
                  style={{ display: selectedImage ? 'none' : 'flex' }}
                >
                  <div className="text-center">
                    <FiPackage size={80} className="text-gray-300 mb-4 mx-auto" />
                    <p className="text-gray-400">Зураг байхгүй</p>
                  </div>
                </div>

                {/* ✅ Image Counter Badge */}
                {product.images && product.images.length > 1 && (
                  <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white text-xs px-3 py-1.5 rounded-full">
                    {product.images.findIndex(img => img === selectedImage) + 1} / {product.images.length}
                  </div>
                )}
              </div>

              {/* ✅ THUMBNAIL GALLERY - Олон зураг */}
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(img)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === img 
                          ? 'border-blue-500 ring-2 ring-blue-200' 
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${product.name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Stock Status Badge */}
              {product.stock_quantity <= 0 ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <FiAlertCircle className="text-red-600" size={24} />
                  <div>
                    <p className="font-semibold text-red-900">Нөөц дууссан</p>
                    <p className="text-sm text-red-600">Энэ бараа одоогоор нөөцгүй байна</p>
                  </div>
                </div>
              ) : product.stock_quantity < 10 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
                  <FiAlertCircle className="text-yellow-600" size={24} />
                  <div>
                    <p className="font-semibold text-yellow-900">Цөөн үлдсэн</p>
                    <p className="text-sm text-yellow-600">
                      Зөвхөн {product.stock_quantity} ширхэг үлдсэн
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <FiCheck className="text-green-600" size={24} />
                  <div>
                    <p className="font-semibold text-green-900">Нөөцтэй</p>
                    <p className="text-sm text-green-600">
                      {product.stock_quantity} ширхэг бэлэн байна
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Product Info */}
          <div>
            <div className="card">
              {/* Title & Category */}
              <div className="mb-6">
                {product.category_name && (
                  <Link
                    to={`/customer/products?category=${product.category_id}`}
                    className="inline-block text-sm text-blue-600 hover:text-blue-700 mb-2"
                  >
                    {product.category_name}
                  </Link>
                )}
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {product.name}
                </h1>
                {product.barcode && (
                  <p className="text-sm text-gray-500">
                    Баркод: {product.barcode}
                  </p>
                )}
              </div>

              {/* Pricing */}
              <div className="border-t border-b border-gray-200 py-6 mb-6">
                <div className="space-y-4">
                  {/* Retail Price */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Жижиглэнгийн үнэ</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ₮{product.retail_price.toLocaleString()}
                      </p>
                    </div>
                    {quantity < product.wholesale_quantity && (
                      <span className="badge badge-gray">Идэвхтэй</span>
                    )}
                  </div>

                  {/* Wholesale Price */}
                  {user?.customer_type === 'wholesale' && (
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg p-4">
                      <div>
                        <p className="text-sm text-blue-600">
                          Бөөний үнэ ({product.wholesale_quantity}+ ширхэг)
                        </p>
                        <p className="text-2xl font-bold text-blue-900">
                          ₮{product.wholesale_price.toLocaleString()}
                        </p>
                      </div>
                      {quantity >= product.wholesale_quantity && (
                        <span className="badge badge-blue">Идэвхтэй</span>
                      )}
                    </div>
                  )}

                  {/* Savings */}
                  {getSavings() > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-700">
                        🎉 Та <span className="font-bold">₮{getSavings().toLocaleString()}</span> хэмнэлээ!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Тоо хэмжээ
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      className="p-3 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiMinus size={20} />
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        if (val >= 1 && val <= product.stock_quantity) {
                          setQuantity(val);
                        }
                      }}
                      className="w-20 text-center border-x border-gray-300 py-3 focus:outline-none"
                      min="1"
                      max={product.stock_quantity}
                    />
                    <button
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= product.stock_quantity}
                      className="p-3 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiPlus size={20} />
                    </button>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Нэгж: {product.unit}</p>
                    <p className="text-sm text-gray-500">
                      Үлдэгдэл: {product.stock_quantity} ширхэг
                    </p>
                  </div>
                </div>

                {/* Wholesale Threshold Info */}
                {user?.customer_type === 'wholesale' && quantity < product.wholesale_quantity && (
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      💡 <span className="font-medium">{product.wholesale_quantity - quantity}</span> ширхэг нэмбэл 
                      бөөний үнээр авах боломжтой!
                    </p>
                  </div>
                )}
              </div>

              {/* Total Price */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Нэгж үнэ:</span>
                  <span className="font-semibold text-gray-900">
                    ₮{getPrice().toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-gray-300 pt-2">
                  <span className="text-lg font-medium text-gray-900">Нийт дүн:</span>
                  <span className="text-2xl font-bold text-blue-600">
                    ₮{getTotalPrice().toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={addToCart}
                  disabled={product.stock_quantity <= 0}
                  className="btn btn-primary w-full py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiShoppingCart className="mr-2" size={20} />
                  Сагсанд нэмэх
                </button>
                <button
                  onClick={() => {
                    addToCart();
                    navigate('/customer/cart');
                  }}
                  disabled={product.stock_quantity <= 0}
                  className="btn btn-outline w-full py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Шууд худалдаж авах
                </button>
              </div>

              {/* Features */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <FiTruck className="text-blue-600" size={24} />
                  <div>
                    <p className="text-xs text-gray-500">Хүргэлт</p>
                    <p className="text-sm font-medium text-gray-900">Үнэгүй</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <FiShield className="text-green-600" size={24} />
                  <div>
                    <p className="text-xs text-gray-500">Баталгаа</p>
                    <p className="text-sm font-medium text-gray-900">6 сар</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <FiTag className="text-purple-600" size={24} />
                  <div>
                    <p className="text-xs text-gray-500">Буцаалт</p>
                    <p className="text-sm font-medium text-gray-900">7 хоног</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <div className="card mt-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Дэлгэрэнгүй мэдээлэл
                </h2>
                <div className="text-gray-600 whitespace-pre-wrap">
                  {product.description}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;